import { CoverLot, CoverLots } from '@/types/CoverLot';
import { LOAD_HOME_DATA_SUCCESS, LOAD_UPCOMING_CATALOGS_SUCCESS } from './actions';
import api from '@/redux/api/coverLots';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_COVER_LOTS_FAIL = 'la/domain/coverLots/LOAD_FAIL';
export const LOAD_COVER_LOTS_REQUEST = 'la/domain/coverLots/LOAD_REQUEST';
export const LOAD_COVER_LOTS_SUCCESS = 'la/domain/coverLots/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

export type CoverLotsState = {
    // dictionary, key is catalog id, value is the cover lot
    byId: { [id: number]: CoverLots };
    // dictionary, key is catalog id, value is the timestamp the cover lot was loaded
    loaded: { [id: number]: number };
    // array of cover lots catalog ids that are in the middle of being loaded
    loading: number[];
};

/* reducer */
const DEFAULT_STATE: CoverLotsState = {
    byId: {},
    loaded: {},
    loading: [],
};

export default function reducer(state = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_COVER_LOTS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.payload),
            };
        case LOAD_COVER_LOTS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_COVER_LOTS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            action.payload.forEach((item) => {
                existing[item.catalogId] = { ...item };
                loaded[item.catalogId] = time;
                loading = difference(loading, [item.catalogId]);
            });
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_UPCOMING_CATALOGS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.smallCoverLots) {
                action.payload.smallCoverLots.forEach((item) => {
                    existing[item.catalogId] = { ...item };
                    loaded[item.catalogId] = time;
                    loading = difference(loading, [item.catalogId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.coverLots;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getCoverLots: (state: any, selector: any) => CoverLots = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || { catalogId: 0, results: [{}, {}, {}] as CoverLot[] }
);

export const getCoverLotItemIds = createSelector([byIdSelector, idSelector], (byId, id) =>
    Boolean(byId[id]) ? { [id]: byId[id].results.map((i) => i.itemId) } : { [id]: [0, 0, 0] }
);

const getLoadTimeForCoverLots = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isCoverLotsLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchCoverLots = (state, catalogId) => {
    if (!catalogId) {
        return false;
    }
    const item = getCoverLots(state, catalogId);
    if (item) {
        const loaded = getLoadTimeForCoverLots(state, catalogId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isCoverLotsLoading(state, catalogId);
    return !loading;
};

const coverLotsToFetch = (state, catalogIds) => {
    return catalogIds.filter((id) => shouldFetchCoverLots(state, id));
};

/* ACTION CREATORS */
const loadCoverLotsFail = (catalogIds, error) => ({
    error: true,
    meta: { catalogIds },
    payload: error,
    type: LOAD_COVER_LOTS_FAIL,
});

const loadCoverLotsRequest = (catalogIds) => ({
    payload: catalogIds,
    type: LOAD_COVER_LOTS_REQUEST,
});

const loadCoverLotsSuccess = (catalogIds, results) => ({
    meta: { actionTime: Date.now(), catalogIds },
    payload: results,
    type: LOAD_COVER_LOTS_SUCCESS,
});

const loadCoverLots = (catalogIds) => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch(loadCoverLotsRequest(catalogIds));
        const response = await api.fetchCoverLots({ catalogIds, deployment });
        return dispatch(loadCoverLotsSuccess(catalogIds, response.data));
    } catch (error) {
        dispatch(loadCoverLotsFail(catalogIds, error));
    }
};

export const fetchCoverLotsIfNeeded = (catalogIds: number[]) => async (dispatch: Function, getState: Function) => {
    const coverLotsToLoad = coverLotsToFetch(getState(), catalogIds);
    if (coverLotsToLoad.length) {
        return dispatch(loadCoverLots(coverLotsToLoad));
    }
    return Promise.resolve();
};
